import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";
import { Input } from "@shared/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import {
  Pencil,
  Plus,
  Search,
  Calendar,
  User,
  Check,
  X,
  Gauge,
  Camera,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { toast, useToast } from "@shared/hooks/use-toast";
import { Alert, AlertDescription } from "@shared/ui/alert";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useReadings,
  useCreateReading,
  useUpdateReading,
  useToggleReadingStatus,
  useOperationTypes,
} from "../../../../hooks";
import { Reading } from "@features/wx/settings/types";

// ====================== TYPES ======================

interface ReadingFormData {
  name: string;
  code: string;
  operationType: string;
  unit: string;
  minRange: string;
  maxRange: string;
  default: boolean;
  photoRequired: boolean;
}

// ====================== HOOKS ======================

// Custom hook for debounced search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// ====================== COMPONENTS ======================

// Reading card component for better reusability
const ReadingCard: React.FC<{
  reading: Reading;
  onEdit: (reading: Reading) => void;
  onToggleActive: (reading: Reading) => void;
  isUpdating: boolean;
  currentReading: Reading | null;
  isToggling: boolean;
}> = ({
  reading,
  onEdit,
  onToggleActive,
  isUpdating,
  currentReading,
  isToggling,
}) => {
  return (
    <Card className="overflow-hidden transition-all hover:shadow-md hover:border-primary/20 group">
      <CardContent className="p-0">
        <div className="p-4 border-b bg-muted/20">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-2">
              <Gauge className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <h4 className="font-medium">{reading.name}</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Type: {reading.workOrderType}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(reading)}
              className="opacity-0 group-hover:opacity-100 hover:bg-background/80"
              disabled={isUpdating}
            >
              {isUpdating && currentReading?.id === reading.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Pencil className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <div className="p-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Gauge className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-muted-foreground">Unit:</span>
              <span className="font-medium">{reading.unit}</span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Range:</span>
              <span className="font-medium">
                {reading.minRange} - {reading.maxRange} {reading.unit}
              </span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Default:</span>
              <span className="font-medium">
                {reading.default ? "Yes" : "No"}
              </span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <Camera className="h-3.5 w-3.5 text-purple-600" />
              <span className="text-muted-foreground">Photo Required:</span>
              <span className="font-medium">
                {reading.photoRequired ? "Yes" : "No"}
              </span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t">
            <div className="flex gap-2 items-center text-sm text-muted-foreground mb-2">
              <User className="h-3.5 w-3.5" />
              <p>{reading.createdBy}</p>
            </div>
            <div className="flex gap-2 items-center text-sm text-muted-foreground mb-4">
              <Calendar className="h-3.5 w-3.5" />
              <p>{reading.createdDate}</p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t">
            <span className="text-sm">
              {reading.isActive ? (
                <span className="flex items-center gap-1.5 text-green-600 bg-green-50 dark:bg-green-950/30 px-2 py-1 rounded-full text-xs font-medium">
                  <Check className="h-3 w-3" /> Active
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-gray-600 bg-gray-50 dark:bg-gray-800/30 px-2 py-1 rounded-full text-xs font-medium">
                  <X className="h-3 w-3" /> Inactive
                </span>
              )}
            </span>
            <Switch
              checked={reading.isActive}
              onCheckedChange={() => onToggleActive(reading)}
              disabled={isToggling}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Empty state component
const EmptyState: React.FC<{
  hasSearchQuery: boolean;
  onAddNew: () => void;
  isLoadingOperationTypes: boolean;
}> = ({ hasSearchQuery, onAddNew, isLoadingOperationTypes }) => {
  return (
    <div className="col-span-full py-12 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
      <div className="flex flex-col items-center gap-2">
        <Search className="h-10 w-10 text-muted-foreground/50" />
        <h4>No readings found</h4>
        <p className="text-sm">
          {hasSearchQuery
            ? "Try adjusting your search or add a new reading"
            : "No readings available"}
        </p>
        <Button
          variant="outline"
          className="mt-2"
          onClick={onAddNew}
          disabled={isLoadingOperationTypes}
        >
          <Plus className="mr-2 h-4 w-4" /> Add New Reading
        </Button>
      </div>
    </div>
  );
};

// ====================== MAIN COMPONENT ======================

const ReadingsTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentReading, setCurrentReading] = useState<Reading | null>(null);

  // Add state to track if we're in cancel mode
  const [isCanceling, setIsCanceling] = useState(false);

  // Get search query from URL
  const searchQuery = searchParams.get("search") || "";

  // Debounce the search input for better UX (but server handles the actual search)
  const [searchInput, setSearchInput] = useState(searchQuery);
  const debouncedSearchInput = useDebounce(searchInput, 500);

  // Update URL when debounced search changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (debouncedSearchInput.trim()) {
      newSearchParams.set("search", debouncedSearchInput.trim());
    } else {
      newSearchParams.delete("search");
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [debouncedSearchInput, searchParams, setSearchParams]);

  // API hooks - now using server-side search
  const { data: readings = [] } = useReadings(searchQuery);
  const { data: operationTypes = [], isLoading: loadingOperationTypes } =
    useOperationTypes();
  const createReadingMutation = useCreateReading();
  const updateReadingMutation = useUpdateReading();
  const toggleStatusMutation = useToggleReadingStatus();

  // Transform operation types for form options
  const operationTypeOptions = useMemo(
    () =>
      operationTypes.map((type) => ({
        value: type.key.toString(),
        label: type.value,
      })),
    [operationTypes],
  );

  // Form fields configuration
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "operationType",
        type: "select",
        label: "Operation Type",
        placeholder: loadingOperationTypes
          ? "Loading operation types..."
          : "Select operation type...",
        required: true,
        disabled: loadingOperationTypes,
        options: operationTypeOptions,
        helperText: "Choose the type of operation for this reading",
      },
      {
        name: "name",
        type: "text",
        label: "Reading Name",
        placeholder: "Enter reading name...",
        required: true,
        minLength: 2,
        maxLength: 50,
        helperText: "Provide a clear, descriptive name for the reading",
      },
      {
        name: "unit",
        type: "text",
        label: "Unit",
        placeholder: "e.g., V, PSI, °C, RPM, kWh",
        required: true,
        minLength: 1,
        maxLength: 20,
        helperText: "Unit of measurement for this reading",
      },
      {
        name: "minRange",
        type: "number",
        label: "Minimum Range",
        placeholder: "0",
        required: true,
        step: 0.01,
        suffix: <Gauge className="h-4 w-4 text-muted-foreground" />,
        helperText: "Minimum acceptable value for this reading",
        // Add validation for minRange as well
        validate: (value: string, formValues: any) => {
          const minRange = parseFloat(value);
          const maxRange = parseFloat(formValues.maxRange);

          if (isNaN(minRange)) {
            return "Please enter a valid minimum range";
          }

          if (!isNaN(maxRange) && minRange >= maxRange) {
            return "Minimum range must be less than maximum range";
          }

          return true;
        },
      },
      {
        name: "maxRange",
        type: "number",
        label: "Maximum Range",
        placeholder: "100",
        required: true,
        step: 0.01,
        suffix: <Gauge className="h-4 w-4 text-muted-foreground" />,
        helperText: "Maximum acceptable value for this reading",
        validate: (value: string, formValues: any) => {
          const maxRange = parseFloat(value);
          const minRange = parseFloat(formValues.minRange);

          if (isNaN(maxRange)) {
            return "Please enter a valid maximum range";
          }

          if (!isNaN(minRange) && maxRange <= minRange) {
            return "Maximum range must be greater than minimum range";
          }

          return true;
        },
      },
      {
        name: "photoRequired",
        type: "switch",
        label: "Photo Required",
        helperText: "Require photo documentation for this reading",
        defaultValue: false,
      },
    ],
    [operationTypeOptions, loadingOperationTypes],
  );

  // Create form instance using FormService - following established pattern
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      operationType: "",
      unit: "",
      minRange: "",
      maxRange: "",
      default: false,
      photoRequired: false,
    },
  });

  // Update form when editing a reading - but not during cancel
  useEffect(() => {
    if (isCanceling) return; // Skip form updates during cancel

    if (currentReading && isEditModalOpen) {
      // Find the operation type key that matches the current reading's operation type
      const currentOperationType = operationTypes.find(
        (op) => op.value === currentReading.workOrderType,
      );

      const populateData = {
        name: currentReading.name,
        operationType: currentOperationType?.key?.toString() || "",
        unit: currentReading.unit,
        minRange: currentReading.minRange.toString(),
        maxRange: currentReading.maxRange.toString(),
        default: currentReading.default || false,
        photoRequired: currentReading.photoRequired || false,
      };

      FormService.populateForm(form, populateData, formFields);
    } else if (isAddModalOpen) {
      const defaultValues = {
        name: "",
        operationType: "",
        unit: "",
        minRange: "",
        maxRange: "",
        default: false,
        photoRequired: false,
      };
      FormService.populateForm(form, defaultValues, formFields);
    }
  }, [
    currentReading,
    isEditModalOpen,
    isAddModalOpen,
    form,
    formFields,
    operationTypes,
    isCanceling,
  ]);

  // Handle field changes - simplified like other components
  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      // Prevent any field changes during cancel
      if (isCanceling) return;
    },
    [isCanceling],
  );

  // Handle form submission
  const handleSubmit = async (data: ReadingFormData) => {
    if (isCanceling) return; // Prevent submit during cancel
    const maxRange = parseFloat(data.maxRange);
    const minRange = parseFloat(data.minRange);

    if (minRange > maxRange) {
      toast({
        title: "Error",
        description: "Minimum range must be less than maximum range",
        variant: "destructive",
      });
      return;
    }

    try {
      if (currentReading) {
        const updatePayload = {
          code: currentReading.code,
          name: data.name.trim(),
          unit: data.unit.trim(),
          default: data.default || false,
          maximum: data.maxRange,
          minimum: data.minRange,
          photoRequired: data.photoRequired || false,
          operationType: parseInt(data.operationType),
          key: currentReading.code,
        };
        // Update existing reading
        await updateReadingMutation.mutateAsync(updatePayload);
        setIsEditModalOpen(false);
      } else {
        const createPayload = {
          code: data.name,
          name: data.name,
          unit: data.unit,
          default: data.default || false,
          maximum: data.maxRange,
          minimum: data.minRange,
          photoRequired: data.photoRequired || false,
          operationType: parseInt(data.operationType),
          key: "",
        };
        // Create new reading
        await createReadingMutation.mutateAsync(createPayload);
        setIsAddModalOpen(false);
      }

      // Reset form state - following established pattern
      FormService.resetForm(form, formFields);
      setCurrentReading(null);
    } catch (error) {
      // Handle error
    }
  };

  const handleReset = useCallback(() => {
    if (isCanceling) return; // Prevent reset during cancel
    FormService.resetForm(form, formFields);
  }, [form, formFields, isCanceling]);

  // Event handlers
  const handleEditClick = useCallback((reading: Reading) => {
    setCurrentReading(reading);
    setIsEditModalOpen(true);
  }, []);

  const handleToggleActive = useCallback(
    async (reading: Reading) => {
      try {
        await toggleStatusMutation.mutateAsync({
          code: reading.id,
          isActive: !reading.isActive,
          name: reading.name,
          operationType: Number(reading.operationType),
        });
      } catch (error) {
        // Handle error
      }
    },
    [toggleStatusMutation],
  );

  // Handle modal close with cancel protection
  const handleAddModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsAddModalOpen(false);
    setCurrentReading(null);

    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  const handleEditModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsEditModalOpen(false);
    setCurrentReading(null);

    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value);
    },
    [],
  );

  return (
    <div className="">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background pt-2 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start flex-col ">
          <h3 className="page-subheading">Readings</h3>
          <p className="page-subtitle">Create and manage Readings.</p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          disabled={createReadingMutation.isPending || loadingOperationTypes}
        >
          {createReadingMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          Add New Reading
        </Button>
      </div>

      {/* Grid */}
      <div className="relative pb-4 mt-2">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search readings..."
          className="pl-8 w-full sm:w-[250px]"
          value={searchInput}
          onChange={handleSearchChange}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.isArray(readings) &&
          readings.map((reading) => (
            <ReadingCard
              key={reading.id}
              reading={reading}
              onEdit={handleEditClick}
              onToggleActive={handleToggleActive}
              isUpdating={updateReadingMutation.isPending}
              currentReading={currentReading}
              isToggling={toggleStatusMutation.isPending}
            />
          ))}

        {Array.isArray(readings) && readings.length === 0 && (
          <EmptyState
            hasSearchQuery={!!searchQuery}
            onAddNew={() => setIsAddModalOpen(true)}
            isLoadingOperationTypes={loadingOperationTypes}
          />
        )}
      </div>

      {/* Add Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={handleAddModalClose}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Add New Reading</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={createReadingMutation.isPending}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleAddModalClose}
                    disabled={createReadingMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={
                      createReadingMutation.isPending || loadingOperationTypes
                    }
                  >
                    {createReadingMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Save Reading"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={handleEditModalClose}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Reading</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={updateReadingMutation.isPending}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleEditModalClose}
                    disabled={updateReadingMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={
                      updateReadingMutation.isPending || loadingOperationTypes
                    }
                  >
                    {updateReadingMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update Reading"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReadingsTab;
